import React from 'react';
import './App.css';
import {BrowserRouter, Route, Switch, matchPath} from "react-router-dom";
import ChooseLanguage from './components/ChooseLanguage/ChooseLanguage';
import Barcode from './components/Barcode/Barcode';
import Prescreen from './components/Prescreen/Prescreen';
import Question from './components/Question/Question';
import Review from './components/Review/Review';
import Error from './components/Error/Error';

// Establish the client version number. REACT_APP_VERSION is provided to the environment by NPM from the package.json's "version" value
export const VERSION_NUMBER = process.env.REACT_APP_NPM_PACKAGE_VERSION + '.' + process.env.REACT_APP_BUILD_NUMBER;

export const PATH_BASE = '/:bcId';
export const PATH_PRESCREEN = PATH_BASE + '/prescreen';
export const PATH_QUESTION = PATH_BASE + '/question/:qId';
export const PATH_REVIEW = PATH_BASE + '/review';
export const PATH_DONOR_PASS = PATH_BASE + '/barcode';
export const PATH_ERROR = '/error';

const XPRESS_STATE_STORAGE_KEY = 'xpressState';
export const XPRESS_STATE_QUESTIONNAIRE = 'questionnaire';
export const XPRESS_STATE_BLOOD_CENTER_PROPERTIES = 'bloodCenterProperties';
export const XPRESS_STATE_TRANSLATION_CONSTANTS = 'translationConstants';
export const XPRESS_STATE_LANGUAGE = 'language';
export const XPRESS_STATE_SEX = 'sex';
export const XPRESS_STATE_SEX_DESCRIPTION = 'sexDescription';
export const XPRESS_STATE_FIRST_TIME_DONOR = 'ftd';
export const XPRESS_STATE_DATE_OF_BIRTH = 'dob';
export const XPRESS_STATE_QUESTIONNAIRE_DATA = 'questionnaireData';
export const XPRESS_STATE_ORIGINAL_QUESTION_ID_LIST = 'originalQuestionIdList';
export const XPRESS_STATE_QUESTIONNAIRE_NODE_LIST = 'questionnaireNodeList';
export const XPRESS_STATE_RESPONSE_BRANCH_OPTIONS = 'responseBranchOptions';
export const XPRESS_STATE_RESPONSES = 'responses';
export const XPRESS_STATE_IS_REVIEW = 'isReview';
export const XPRESS_STATE_DONOR_PASS = 'donorPass';
export const XPRESS_STATE_VIEWED_PAGES = 'viewedPages';
export const XPRESS_STATE_SEX_OPTIONS = 'sexOptions';

export default class App extends React.Component
{
    constructor(props) {
        super(props);

        let xpressStateDefault = {
            language: null,
            translationConstants: null,
            dob: null,
            ftd: true, // Hardcoded to indicate the donor is a first time donor since the FTD prompt will not be presented
            sex: null,
            sexDescription: null, // Store the description of the donor's selected sex (HD #10194)
            questionnaire: null,
            bloodCenterProperties: null,
            questionnaireData: null,
            originalQuestionIdList: null,
            questionnaireNodeList: null,
            responseBranchOptions: null,
            isReview: false,
            responses: [],
            donorPass: null,
            viewedPages: [],
            sexOptions: null // Stores the blood center's configured sex options (HD #10194)
        };

        // If visiting the base URL for a center ID, reset to the default state. Otherwise, retrieve the state from the component variable or local storage
        let xpressState;
        if(matchPath(window.location.pathname, { path: "/:bcId/", exact: true, strict: false})) {
            xpressState = xpressStateDefault;
        }
        else {
            xpressState = this.getXpressState();
            if(!xpressState) {
                xpressState = xpressStateDefault;
            }
        }

        this.setXpressState(xpressState);
    }

    getXpressState = () => {
        // Return the component variable if is has a value. Otherwise, return the persisted state from local storage
        let xpressState = this.xpressState;
        if(xpressState) {
            return xpressState;
        }

        xpressState = window.sessionStorage.getItem(XPRESS_STATE_STORAGE_KEY); // Changed to use sessionStorage instead of localStorage (QA-231)
        if(xpressState) {
            xpressState = JSON.parse(xpressState);
        }

        return xpressState;
    }

    setXpressState = (xpressState) => {
        this.xpressState = xpressState;
        window.sessionStorage.setItem(XPRESS_STATE_STORAGE_KEY, JSON.stringify(xpressState)); // Changed to use sessionStorage instead of localStorage (QA-231)
    }

    updateXpressStateValue = (key, value) => {
        let xpressState = this.getXpressState();
        if(xpressState) {
            xpressState[key] = value;
        }

        this.setXpressState(xpressState);
    }

    // Performs evaluations prior to loading the requested route/component
    checkRoute = (renderComponent) => {
        let xpressState = this.getXpressState();

        // If the donor pass has been generated and the user navigates to a path outside of the donor pass or error paths, redirect to the base path to start over
        if (xpressState.donorPass
            && (!matchPath(window.location.pathname, { path: PATH_DONOR_PASS,  exact: true,  strict: false })
                && !matchPath(window.location.pathname, {path: PATH_ERROR, exact: true, strict: false}))) {
            window.location.href = PATH_BASE.replace(':bcId', xpressState.bloodCenterProperties.bloodCenterId);
            return null;
        }

        return renderComponent;
    }

    render()
    {
        let xpressState = this.getXpressState();

        return (
            <BrowserRouter>
                <Switch>
                    <Route path="/:bcId" exact render={(props) => { return this.checkRoute(<ChooseLanguage {...props} xpressState={xpressState} fnUpdateXpressStateValue={this.updateXpressStateValue} />); }}/>
                    <Route path="/:bcId/prescreen" render={(props) => { return this.checkRoute(<Prescreen {...props} xpressState={xpressState} fnUpdateXpressStateValue={this.updateXpressStateValue} />); }} />
                    <Route path="/:bcId/question/:qId" render={(props) => { return this.checkRoute(<Question {...props} xpressState={xpressState} fnUpdateXpressStateValue={this.updateXpressStateValue} />); }}/>
                    <Route path="/:bcId/review" render={(props) => { return this.checkRoute(<Review {...props} xpressState={xpressState} fnUpdateXpressStateValue={this.updateXpressStateValue} />); }}/>
                    <Route path="/:bcId/barcode" render={(props) => { return this.checkRoute(<Barcode {...props} xpressState={xpressState} fnUpdateXpressStateValue={this.updateXpressStateValue} />); }}/>
                    <Route path="/:bcId/error" render={(props) => <Error{...props} xpressState={xpressState} />}/>
                    <Route component={Error}/>
                </Switch>
            </BrowserRouter>
        );
    }
}